import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Calendar, Clock, Video, Award, Users, Phone, MessageCircle, Activity, Eye, CheckCircle2, Sparkles, Star, Brain, Heart, Zap } from 'lucide-react';
import { TCMHero } from '../components/TCMHero';
import { TCMSection } from '../components/TCMSection';
import { DiagnosticCard } from '../components/DiagnosticCard';
import { TestimonialCard } from '../components/TestimonialCard';
export function TCMLanding() {
  const testimonials = [{
    name: 'Dr. Priya Sharma',
    location: 'Mumbai, Maharashtra',
    profession: 'Ayurvedic Practitioner',
    testimonial: "Dr. Kapoor's teaching of tongue diagnosis transformed my practice completely. The Maciocia system is explained with such clarity that I can now identify patterns I previously missed. My diagnostic accuracy has improved tremendously."
  }, {
    name: 'Rajesh Kumar',
    location: 'Bangalore, Karnataka',
    profession: 'Acupuncturist',
    testimonial: "The pulse diagnosis module by Dr. Jimmy Chang's method is absolutely brilliant. Learning to feel 28 different pulse qualities seemed impossible at first, but the systematic approach and live demonstrations made it achievable. Highly recommended!"
  }, {
    name: 'Dr. Anjali Desai',
    location: 'Pune, Maharashtra',
    profession: 'Integrative Medicine Doctor',
    testimonial: 'This course bridges ancient wisdom with practical application beautifully. The case studies and Q&A sessions were invaluable. I now integrate tongue and pulse diagnosis confidently in my clinical practice with excellent patient outcomes.'
  }, {
    name: 'Vikram Patel',
    location: 'Delhi NCR',
    profession: 'TCM Student',
    testimonial: "As a student, this mastercourse gave me the depth I was seeking. Dr. Kapoor's expertise in correlating Yin-Yang theory with diagnostic signs is exceptional. The lifetime access to recordings means I can revisit concepts whenever needed."
  }, {
    name: 'Dr. Meera Reddy',
    location: 'Hyderabad, Telangana',
    profession: 'Naturopathy Physician',
    testimonial: "The combination of Giovanni Maciocia's tongue system and Dr. Chang's pulse method provides a complete diagnostic framework. The course structure is well-paced, and the practical exercises helped solidify my understanding immensely."
  }, {
    name: 'Arjun Nair',
    location: 'Kochi, Kerala',
    profession: 'Holistic Healer',
    testimonial: 'What sets this course apart is the emphasis on pattern differentiation and clinical reasoning. I can now diagnose Liver Qi Stagnation, Spleen deficiency, and other patterns with confidence. Worth every rupee invested!'
  }, {
    name: 'Suresh Gowda',
    location: 'Mysore, Karnataka',
    profession: 'Ayurvedic Physician',
    testimonial: "Coming from an Ayurvedic background, this course beautifully complemented my existing knowledge. The tongue coating analysis and its correlation with doshas opened new diagnostic dimensions. Dr. Kapoor's teaching style is clear and engaging."
  }, {
    name: 'Dr. Kavita Rao',
    location: 'Mangalore, Karnataka',
    profession: 'Integrative Health Consultant',
    testimonial: 'The live Q&A sessions were incredibly valuable. Dr. Kapoor patiently answered every question and provided real clinical examples. I now feel confident applying both tongue and pulse diagnosis in my daily practice with patients.'
  }, {
    name: 'Amit Singh',
    location: 'Chandigarh, Punjab',
    profession: 'Acupuncture Specialist',
    testimonial: 'The systematic approach to pulse diagnosis changed everything for me. I can now differentiate between wiry, slippery, and choppy pulses with precision. This has dramatically improved my point selection and treatment outcomes.'
  }, {
    name: 'Dr. Neha Gupta',
    location: 'Jaipur, Rajasthan',
    profession: 'TCM Practitioner',
    testimonial: 'Outstanding course! The integration of Five Elements theory with diagnostic techniques is masterfully done. The practical exercises and case studies made complex concepts easy to grasp and apply clinically.'
  }, {
    name: 'Ravi Malhotra',
    location: 'Lucknow, Uttar Pradesh',
    profession: 'Holistic Medicine Doctor',
    testimonial: "Dr. Kapoor's depth of knowledge in TCM tongue diagnosis is remarkable. The course materials are comprehensive, and the lifetime recording access means I can keep learning. My patients have noticed the improvement in my diagnostic precision."
  }, {
    name: 'Pradeep Shetty',
    location: 'Udupi, Karnataka',
    profession: 'Traditional Healer',
    testimonial: "The way this course connects organ patterns with pulse and tongue signs is brilliant. I've been practicing for years, but this mastercourse refined my skills significantly. The early bird pricing made it even more valuable!"
  }];
  const tongueImages = [{
    url: 'https://cdn.magicpatterns.com/uploads/h1yuxPWTmpopDZ4Zh15eBf/image.png',
    caption: 'Normal Healthy Tongue',
    description: 'Pale red body, thin white coating'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/fBq69TEhtWe5TsnxQ2zmMa/image.png',
    caption: 'Pale Tongue',
    description: 'Indicates Qi & Blood deficiency'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/pknCH3JiqbhJQPqnKCskvh/image.png',
    caption: 'Red Tongue with Cracks',
    description: 'Heat pattern, Yin deficiency'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/hYXhz1PRmtnviGvut1ZLE2/image.png',
    caption: 'Thick White Coating',
    description: 'Cold-Damp or Phlegm accumulation'
  }];
  return <div className="min-h-screen bg-[#F5F1E8] text-[#1a1a1a] font-sans selection:bg-[#C8102E]/20 selection:text-[#C8102E]" data-id="element-78">
      {/* Global Styles for Fonts */}
      <style data-id="element-79">{`
        @import url('https://fonts.googleapis.com/css2?family=Crimson+Text:ital,wght@0,400;0,600;0,700;1,400&family=Noto+Serif+SC:wght@300;400;700&display=swap');
        
        .font-serif {
          font-family: 'Crimson Text', serif;
        }
        .writing-vertical-rl {
          writing-mode: vertical-rl;
        }
      `}</style>

      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#F5F1E8]/90 backdrop-blur-md border-b border-[#1a1a1a]/5 py-4 px-6 md:px-12 flex justify-between items-center" data-id="element-80">
        <div className="text-xl font-serif font-bold tracking-wider text-[#1a1a1a]" data-id="element-81">
          AIIMR <span className="text-[#C8102E]" data-id="element-82">TCM</span>
        </div>
        <a href="#contact" className="text-sm uppercase tracking-widest font-medium hover:text-[#C8102E] transition-colors" data-id="element-83">
          Register
        </a>
      </nav>

      <main data-id="element-84">
        <TCMHero data-id="element-85" />

        {/* Overview Section */}
        <TCMSection pattern="bamboo" hasDivider data-id="element-86">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-87">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-88">
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 text-[#1a1a1a]" data-id="element-89">
                Classical Diagnostic Arts
              </h2>
              <p className="text-lg leading-relaxed text-[#1a1a1a]/80 font-serif mb-6" data-id="element-90">
                We're delighted to announce an exclusive opportunity for serious
                students and practitioners of Traditional Chinese Medicine. This
                2-month program blends detailed observation, pattern
                recognition, and clinical application for precise, intuitive
                diagnosis.
              </p>
              <div className="flex flex-col gap-4" data-id="element-91">
                <div className="flex items-center gap-3 text-[#1a1a1a]/70" data-id="element-92">
                  <Clock className="text-[#C8102E]" size={20} data-id="element-93" />
                  <span data-id="element-94">Duration: Approx. 2 Months</span>
                </div>
                <div className="flex items-center gap-3 text-[#1a1a1a]/70" data-id="element-95">
                  <Calendar className="text-[#C8102E]" size={20} data-id="element-96" />
                  <span data-id="element-97">3 Classes per Week (3 Hours each)</span>
                </div>
                <div className="flex items-center gap-3 text-[#1a1a1a]/70" data-id="element-98">
                  <Video className="text-[#C8102E]" size={20} data-id="element-99" />
                  <span data-id="element-100">LIVE Online Interactive Sessions</span>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative p-8 border border-[#1a1a1a]/10 bg-white/30 rounded-sm" data-id="element-101">
              <h3 className="text-xl font-serif font-bold mb-4 flex items-center gap-2" data-id="element-102">
                <Sparkles size={20} className="text-[#D4AF37]" data-id="element-103" />
                Prerequisite Knowledge
              </h3>
              <p className="text-[#1a1a1a]/70 mb-4" data-id="element-104">
                Solid understanding of the following is essential for this
                advanced course:
              </p>
              <ul className="space-y-2 text-sm font-medium text-[#1a1a1a]/80" data-id="element-105">
                <li className="flex items-center gap-2" data-id="element-106">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#1a1a1a]/40" data-id="element-107" />
                  Yin-Yang Theory
                </li>
                <li className="flex items-center gap-2" data-id="element-108">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#1a1a1a]/40" data-id="element-109" />
                  Five Elements (Wood, Fire, Earth, Metal, Water)
                </li>
                <li className="flex items-center gap-2" data-id="element-110">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#1a1a1a]/40" data-id="element-111" />
                  Organ Patterns (Zang-Fu relationships)
                </li>
              </ul>
            </motion.div>
          </div>
        </TCMSection>

        {/* Detailed Curriculum */}
        <TCMSection id="details" className="bg-[#1a1a1a]/[0.02]" pattern="cloud" data-id="element-112">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-113">
            <h2 className="text-3xl md:text-5xl font-serif font-bold mb-4" data-id="element-114">
              Course Curriculum
            </h2>
            <p className="text-[#1a1a1a]/60 italic font-serif text-xl" data-id="element-115">
              Two legendary systems, one comprehensive mastercourse.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-116">
            <DiagnosticCard title="Tongue Diagnosis" subtitle="Giovanni Maciocia System" description="Master the tongue as a dynamic mirror of internal health. Learn to interpret body color, shape, coating, and movements for precise pattern differentiation." icon={Eye} color="red" imageUrl="https://cdn.magicpatterns.com/uploads/bL4gEyB5csRgwRahxCC3t9/image.png" details={['Body color (pale, red, purple) & Shape (swollen, thin, cracked)', 'Coating analysis (thick/thin, white/yellow/greasy)', 'Interpret Qi/Blood/Yin deficiencies & Heat/Damp-Phlegm', 'Organ-specific disharmonies (Spleen Qi deficiency, Heart Blood stasis)', 'Acute vs. chronic signs & emotional correlations']} data-id="element-117" />

            <DiagnosticCard title="Pulse Diagnosis" subtitle="Dr. Jimmy Chang Method" description="Unlock pulse reading as 'energy made easy'. Identify over 28 pulse qualities across 3 levels and 6 positions per wrist to diagnose channel blockages." icon={Activity} color="green" delay={0.2} imageUrl="https://cdn.magicpatterns.com/uploads/gcJwbdA4StNPgVpydsND7k/image.png" details={['28+ pulse qualities across 3 levels (superficial/middle/deep)', 'Focus on rapid/slow, wiry/slippery, choppy/flooding pulses', 'Diagnose energetic stagnation (Liver Qi Stagnation, Kidney Yang deficiency)', 'Clinical application for acupuncture & herbal strategies', 'Real-time sensitivity training integrating Zang-Fu patterns']} data-id="element-118" />
          </div>
        </TCMSection>

        {/* Tongue Diagnosis Visual Gallery */}
        <TCMSection pattern="bamboo" data-id="element-119">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-120">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4" data-id="element-121">
              Tongue Diagnosis Examples
            </h2>
            <p className="text-[#1a1a1a]/60 font-serif text-lg" data-id="element-122">
              Learn to identify patterns through visual observation and
              systematic analysis
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-123">
            {tongueImages.map((image, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: index * 0.1
          }} className="group" data-id="element-124">
                <div className="relative overflow-hidden rounded-sm border-2 border-[#C8102E]/20 bg-white shadow-lg" data-id="element-125">
                  <div className="aspect-square overflow-hidden" data-id="element-126">
                    <img src={image.url} alt={image.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-127" />
                  </div>
                </div>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.5
        }} className="mt-12 text-center" data-id="element-128">
            <p className="text-[#1a1a1a]/70 font-serif italic max-w-2xl mx-auto" data-id="element-129">
              "In this course, you'll learn to identify dozens of tongue
              patterns and correlate them with organ systems, constitutional
              types, and treatment strategies."
            </p>
          </motion.div>
        </TCMSection>

        {/* Pulse Diagnosis Deep Dive */}
        <TCMSection className="bg-[#1a1a1a]/[0.02]" data-id="element-130">
          <div className="max-w-4xl mx-auto" data-id="element-131">
            <div className="text-center mb-12" data-id="element-132">
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4" data-id="element-133">
                Master the Art of Pulse Reading
              </h2>
              <p className="text-[#1a1a1a]/60 font-serif text-lg" data-id="element-134">
                Develop sensitivity to subtle energetic shifts through the
                radial artery
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-135">
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8
            }} className="relative" data-id="element-136">
                <img src="https://cdn.magicpatterns.com/uploads/4AFevZdemFCGVZhZrLFoMg/image.png" alt="Pulse Diagnosis Technique" className="w-full h-auto rounded-sm border border-[#00A86B]/20 shadow-lg" data-id="element-137" />
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} className="flex flex-col justify-center" data-id="element-138">
                <div className="space-y-6" data-id="element-139">
                  <div className="flex items-start gap-4" data-id="element-140">
                    <div className="p-3 bg-[#00A86B]/10 rounded-full" data-id="element-141">
                      <Brain className="text-[#00A86B]" size={24} data-id="element-142" />
                    </div>
                    <div data-id="element-143">
                      <h4 className="font-serif font-bold text-lg mb-2" data-id="element-144">
                        Three Levels of Depth
                      </h4>
                      <p className="text-[#1a1a1a]/70" data-id="element-145">
                        Learn to palpate superficial, middle, and deep levels to
                        assess Wei Qi, Ying Qi, and organ vitality
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-146">
                    <div className="p-3 bg-[#00A86B]/10 rounded-full" data-id="element-147">
                      <Heart className="text-[#00A86B]" size={24} data-id="element-148" />
                    </div>
                    <div data-id="element-149">
                      <h4 className="font-serif font-bold text-lg mb-2" data-id="element-150">
                        Six Positions Per Wrist
                      </h4>
                      <p className="text-[#1a1a1a]/70" data-id="element-151">
                        Map the 12 organ systems through Cun, Guan, and Chi
                        positions on both radial arteries
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-152">
                    <div className="p-3 bg-[#00A86B]/10 rounded-full" data-id="element-153">
                      <Zap className="text-[#00A86B]" size={24} data-id="element-154" />
                    </div>
                    <div data-id="element-155">
                      <h4 className="font-serif font-bold text-lg mb-2" data-id="element-156">
                        28+ Pulse Qualities
                      </h4>
                      <p className="text-[#1a1a1a]/70" data-id="element-157">
                        Differentiate floating, sinking, slow, rapid, wiry,
                        slippery, choppy, and many more pulse types
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </TCMSection>

        {/* Demo Videos from Previous Classes */}
        <TCMSection pattern="cloud" data-id="element-158">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-159">
            <div className="flex items-center justify-center gap-2 mb-4" data-id="element-160">
              <Video className="text-[#C8102E]" size={28} data-id="element-161" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4" data-id="element-162">
              Live Class Demonstrations
            </h2>
            <p className="text-[#1a1a1a]/60 font-serif text-lg" data-id="element-163">
              Experience the teaching style and depth of our previous sessions
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto" data-id="element-164">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group" data-id="element-165">
              <div className="relative overflow-hidden rounded-sm border-2 border-[#C8102E]/20 bg-white shadow-lg" data-id="element-166">
                <div className="relative aspect-video overflow-hidden bg-[#1a1a1a]/5" data-id="element-167">
                  <iframe src="https://www.youtube.com/embed/yyldZfG79MU" title="TCM Diagnostic Class Demo" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-168" />
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="group" data-id="element-169">
              <div className="relative overflow-hidden rounded-sm border-2 border-[#00A86B]/20 bg-white shadow-lg" data-id="element-170">
                <div className="relative aspect-video overflow-hidden bg-[#1a1a1a]/5" data-id="element-171">
                  <iframe src="https://www.youtube.com/embed/-bmLUloB8G8" title="TCM Diagnostic Class Demo" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-172" />
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="group" data-id="element-173">
              <div className="relative overflow-hidden rounded-sm border-2 border-[#D4AF37]/20 bg-white shadow-lg" data-id="element-174">
                <div className="relative aspect-video overflow-hidden bg-[#1a1a1a]/5" data-id="element-175">
                  <iframe src="https://www.youtube.com/embed/7wrq0D8TLXg" title="TCM Diagnostic Class Demo" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-176" />
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="group" data-id="element-177">
              <div className="relative overflow-hidden rounded-sm border-2 border-[#C8102E]/20 bg-white shadow-lg" data-id="element-178">
                <div className="relative aspect-video overflow-hidden bg-[#1a1a1a]/5" data-id="element-179">
                  <iframe src="https://www.youtube.com/embed/qOQlj9c3XFs" title="TCM Diagnostic Class Demo" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-180" />
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="mt-12 text-center" data-id="element-181">
            <p className="text-[#1a1a1a]/70 font-serif italic max-w-2xl mx-auto mb-6" data-id="element-182">
              "These recordings showcase Dr. Kapoor's teaching methodology—clear
              explanations, live demonstrations, and interactive Q&A sessions
              that make complex concepts accessible."
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-183">
              <a href="#contact" className="inline-flex items-center gap-2 px-6 py-3 bg-[#C8102E] text-[#F5F1E8] font-medium uppercase tracking-widest hover:bg-[#A00C24] transition-colors duration-300 shadow-lg" data-id="element-184">
                <Video size={18} data-id="element-185" />
                <span data-id="element-186">Join the Next Batch</span>
              </a>
              <a href="https://www.youtube.com/@aiimraethe" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#1a1a1a]/20 text-[#1a1a1a] font-medium uppercase tracking-widest hover:bg-[#1a1a1a]/5 transition-colors duration-300" data-id="element-187">
                <Video size={18} data-id="element-188" />
                <span data-id="element-189">Visit Our YouTube Channel</span>
              </a>
            </div>
          </motion.div>
        </TCMSection>

        {/* Who Can Join & Highlights */}
        <TCMSection data-id="element-190">
          <div className="grid md:grid-cols-2 gap-16" data-id="element-191">
            <div data-id="element-192">
              <h3 className="text-2xl font-serif font-bold mb-8 flex items-center gap-3" data-id="element-193">
                <Users className="text-[#1a1a1a]" data-id="element-194" />
                Who Can Join
              </h3>
              <ul className="space-y-6" data-id="element-195">
                {['Practicing acupuncturists, TCM/herbalists, and integrative doctors', 'Ayurveda & naturopathy professionals expanding diagnostics', 'TCM students and holistic healers (with prerequisites)'].map((item, i) => <motion.li key={i} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="flex items-start gap-4" data-id="element-196">
                    <CheckCircle2 className="text-[#00A86B] flex-shrink-0 mt-1" size={20} data-id="element-197" />
                    <span className="text-[#1a1a1a]/80 text-lg leading-relaxed" data-id="element-198">
                      {item}
                    </span>
                  </motion.li>)}
              </ul>
            </div>

            <div data-id="element-199">
              <h3 className="text-2xl font-serif font-bold mb-8 flex items-center gap-3" data-id="element-200">
                <Award className="text-[#1a1a1a]" data-id="element-201" />
                Course Highlights
              </h3>
              <div className="grid grid-cols-1 gap-4" data-id="element-202">
                {['Live demos, case studies, and Q&A', 'Practical exercises from course materials', 'Completion certificate', 'Lifetime access to recordings'].map((item, i) => <motion.div key={i} initial={{
                opacity: 0,
                y: 10
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.2 + i * 0.1
              }} className="p-4 bg-white border border-[#1a1a1a]/10 flex items-center gap-3 shadow-sm" data-id="element-203">
                    <div className="w-2 h-2 bg-[#D4AF37] rounded-full" data-id="element-204" />
                    <span className="font-medium text-[#1a1a1a]/90" data-id="element-205">
                      {item}
                    </span>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </TCMSection>

        {/* Student Testimonials */}
        <TCMSection className="bg-[#1a1a1a]/[0.02]" pattern="bamboo" data-id="element-206">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-207">
            <div className="flex items-center justify-center gap-2 mb-4" data-id="element-208">
              <Star className="text-[#D4AF37] fill-[#D4AF37]" size={24} data-id="element-209" />
              <Star className="text-[#D4AF37] fill-[#D4AF37]" size={24} data-id="element-210" />
              <Star className="text-[#D4AF37] fill-[#D4AF37]" size={24} data-id="element-211" />
              <Star className="text-[#D4AF37] fill-[#D4AF37]" size={24} data-id="element-212" />
              <Star className="text-[#D4AF37] fill-[#D4AF37]" size={24} data-id="element-213" />
            </div>
            <h2 className="text-3xl md:text-5xl font-serif font-bold mb-4" data-id="element-214">
              Student Experiences
            </h2>
            <p className="text-[#1a1a1a]/60 italic font-serif text-xl" data-id="element-215">
              Hear from practitioners who transformed their diagnostic skills
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-216">
            {testimonials.map((testimonial, index) => <TestimonialCard key={index} {...testimonial} delay={index * 0.05} data-id="element-217" />)}
          </div>
        </TCMSection>

        {/* Pricing */}
        <TCMSection className="bg-[#C8102E]/5" pattern="cloud" data-id="element-218">
          <div className="max-w-4xl mx-auto text-center" data-id="element-219">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-12" data-id="element-220">
              Energy Exchange
            </h2>

            <div className="relative inline-block group" data-id="element-221">
              <div className="absolute inset-0 bg-[#C8102E] transform rotate-1 rounded-sm opacity-10 group-hover:rotate-2 transition-transform duration-500" data-id="element-222" />
              <div className="relative bg-white p-8 md:p-12 border border-[#C8102E]/20 shadow-xl max-w-lg mx-auto" data-id="element-223">
                <div className="absolute top-0 right-0 bg-[#D4AF37] text-white text-xs font-bold px-3 py-1 uppercase tracking-widest transform translate-x-2 -translate-y-2" data-id="element-224">
                  Early Bird
                </div>

                <h3 className="text-xl text-[#1a1a1a]/60 uppercase tracking-widest mb-2" data-id="element-225">
                  Tongue + Pulse Combo
                </h3>
                <div className="flex items-baseline justify-center gap-3 mb-6" data-id="element-226">
                  <span className="text-5xl font-serif font-bold text-[#C8102E]" data-id="element-227">
                    ₹18,000
                  </span>
                  <span className="text-xl text-[#1a1a1a]/40 line-through decoration-red-500/50" data-id="element-228">
                    ₹20,000
                  </span>
                </div>

                <p className="text-[#00A86B] font-medium mb-8 flex items-center justify-center gap-2" data-id="element-229">
                  <Sparkles size={16} data-id="element-230" />
                  Offer valid till 15th Jan 2026
                </p>

                <div className="w-full h-px bg-[#1a1a1a]/10 mb-8" data-id="element-231" />

                <p className="text-[#1a1a1a]/70 mb-8 italic" data-id="element-232">
                  "Transform your diagnostic precision with ancient wisdom."
                </p>

                <a href="#contact" className="block w-full py-4 bg-[#1a1a1a] text-[#F5F1E8] font-medium uppercase tracking-widest hover:bg-[#C8102E] transition-colors duration-300" data-id="element-233">
                  Secure Your Seat
                </a>
              </div>
            </div>
          </div>
        </TCMSection>

        {/* Contact / Footer */}
        <footer id="contact" className="bg-[#1a1a1a] text-[#F5F1E8] py-20 px-6 relative overflow-hidden" data-id="element-234">
          <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-235">
            <svg viewBox="0 0 100 100" className="w-full h-full fill-current text-[#F5F1E8]" data-id="element-236">
              <pattern id="pattern-circles" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse" data-id="element-237">
                <circle cx="2" cy="2" r="1" data-id="element-238" />
              </pattern>
              <rect x="0" y="0" width="100%" height="100%" fill="url(#pattern-circles)" data-id="element-239" />
            </svg>
          </div>

          <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-240">
            <BookOpen size={40} className="mx-auto mb-8 text-[#D4AF37]" strokeWidth={1} data-id="element-241" />

            <h2 className="text-3xl md:text-5xl font-serif font-bold mb-6" data-id="element-242">
              Begin Your Journey
            </h2>
            <p className="text-xl text-[#F5F1E8]/60 mb-12 max-w-2xl mx-auto font-serif" data-id="element-243">
              Limited seats available. Contact us to register or join the
              WhatsApp group for updates.
            </p>

            <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-16" data-id="element-244">
              <div className="text-center" data-id="element-245">
                <p className="text-[#D4AF37] text-sm uppercase tracking-widest mb-2" data-id="element-246">
                  Course Director
                </p>
                <p className="text-xl font-serif" data-id="element-247">Dr. Amit Kapoor</p>
                <p className="text-[#F5F1E8]/50 text-sm" data-id="element-248">
                  PhD in TCM Tongue Diagnosis
                </p>
              </div>

              <div className="hidden md:block w-px h-16 bg-[#F5F1E8]/20" data-id="element-249" />

              <a href="tel:+918303699990" className="flex items-center gap-3 px-6 py-3 border border-[#F5F1E8]/20 hover:bg-[#F5F1E8]/10 transition-colors rounded-sm" data-id="element-250">
                <Phone size={20} className="text-[#00A86B]" data-id="element-251" />
                <span data-id="element-252">+91 83036 99990</span>
              </a>

              <a href="https://chat.whatsapp.com/CMmsr9Ozzgw01iSCC10ZMG?mode=hqrt1" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-6 py-3 bg-[#00A86B] hover:bg-[#008f5b] transition-colors rounded-sm text-white shadow-lg shadow-green-900/20" data-id="element-253">
                <MessageCircle size={20} data-id="element-254" />
                <span data-id="element-255">Join WhatsApp Group</span>
              </a>
            </div>

            <div className="text-[#F5F1E8]/30 text-sm pt-8 border-t border-[#F5F1E8]/10" data-id="element-256">
              © 2026 AIIMR. All rights reserved. Traditional Chinese Medicine
              Education.
            </div>
          </div>
        </footer>
      </main>
    </div>;
}